import { useUserData } from "graphql/user";
import {
  Banknote,
  ClipboardPenLine,
  FileClock,
  FileText,
  HandCoins,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import { useMemo } from "react";

export type PromptItem = {
  prompt: string;
  icon: LucideIcon;
};

export const usePrompts = () => {
  const { t } = useI18n();
  const { user } = useUserData();

  const userRoles = user?.userRoles ?? [];

  const hasPlatformRole = userRoles.some((ur) => ur.role.isPlatform);

  const hasCompanyRole = userRoles.some(
    (ur) => !ur.role.isPlatform && ur.company,
  );

  const suggestedQuestions = useMemo<PromptItem[]>(
    () => [
      {
        prompt: t("apps.admin.chatAI.suggestedQuestions.0"),
        icon: Users,
      },
      {
        prompt: t("apps.admin.chatAI.suggestedQuestions.1"),
        icon: HandCoins,
      },
      {
        prompt: t("apps.admin.chatAI.suggestedQuestions.2"),
        icon: Banknote,
      },
    ],
    [t],
  );

  const reports = useMemo<PromptItem[]>(() => {
    const allReports: PromptItem[] = [];

    if (hasPlatformRole) {
      allReports.push(
        {
          prompt: t("apps.admin.chatAI.reports.platform.onboardingStatus"),
          icon: ClipboardPenLine,
        },
        {
          prompt: t("apps.admin.chatAI.reports.platform.contributions12m"),
          icon: FileText,
        },
        {
          prompt: t("apps.admin.chatAI.reports.platform.withdrawals12m"),
          icon: FileClock,
        },
      );
    } else if (hasCompanyRole) {
      allReports.push(
        {
          prompt: t(
            "apps.admin.chatAI.reports.company.employeeContributions3m",
          ),
          icon: Users,
        },
        {
          prompt: t("apps.admin.chatAI.reports.company.benefitsAssignments"),
          icon: FileText,
        },
        {
          prompt: t("apps.admin.chatAI.reports.company.pendingPayments"),
          icon: FileClock,
        },
      );
    }

    return allReports;
  }, [t, hasPlatformRole, hasCompanyRole]);

  return {
    suggestedQuestions,
    reports,
    hasReports: reports.length > 0,
  };
};
